import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";

import { api } from "../../../api";
import { showNotification } from "../../../actions/index";
import { extractCenterId } from "../../../utils/center";

/*
 * Voyant + bascule du dispatch auto, sur l'écran /dispatch, sans passer par l'admin.
 * L'état vient des metas de /tasks/state/ (rafraîchies par le polling). Le droit de basculer vient
 * de l'utilisateur, comme les boutons de l'en-tête conditionnés par un droit (« Logs », « Demandes ») :
 * `/admin-user/` renvoie `groups.can_activate_dispatch_auto`, décidé côté API avec la même règle que
 * l'endpoint de bascule. Sans le droit, l'état reste visible mais les cases sont grisées.
 * Optimiste : l'état local suit le clic tout de suite, puis se recale sur les metas.
 * Les cases sont gelées pour l'instant (READ_ONLY) : voyant seul, la bascule passe par l'admin.
 */
// Bascule gelée pour l'instant : l'état reste affiché, la modification passe par l'admin.
// Pour la rouvrir, repasser cette constante à false.
const READ_ONLY = true;

const SWITCHES = [
  { key: "enabled", meta: "dispatch_auto_enabled", label: "Dispatch auto",
    on: "Dispatch auto actif : les tournées gelées sont créées automatiquement.",
    off: "Dispatch auto inactif : aucune tournée n'est créée automatiquement." },
  { key: "first_enabled", meta: "dispatch_auto_first_enabled", label: "Attribution auto livreurs",
    on: "Une tournée gelée toute préparée est attribuée d'elle-même à un livreur (FIRST).",
    off: "Les tournées gelées attendent le dispatcheur, personne ne les prend automatiquement." },
];

// Centre courant : comme le reste de l'app (utilisateur), repli sur la session (cf. DQPs.js) — les
// metas de /tasks/state/ ne portent pas d'id de centre.
const currentCenterId = (state) => {
  const fromUser = extractCenterId(state.user);
  if (fromUser) { return fromUser; }
  const fromSession = parseInt(window.sessionStorage.getItem("flow_current_center"), 10);
  return Number.isNaN(fromSession) ? null : fromSession;
};

// Deux branches portent des metas selon l'écran : `m` (écran principal) et `dispatch` (dry-run).
// Même repli que RainBonus : `m` par défaut, `dispatch` quand elle porte la clé.
const currentMetas = (state) =>
  state.dispatch.metas.dispatch_auto_enabled !== undefined ? state.dispatch.metas : state.m.metas;

export const DispatchAutoSwitch = connect((state) => ({
  centerId: currentCenterId(state),
  metaEnabled: currentMetas(state).dispatch_auto_enabled,
  metaFirst: currentMetas(state).dispatch_auto_first_enabled,
  canActivate: !!(state.user && state.user.groups && state.user.groups.can_activate_dispatch_auto),
}))(({ centerId, metaEnabled, metaFirst, canActivate, dispatch }) => {
  const [values, setValues] = useState({ enabled: !!metaEnabled, first_enabled: !!metaFirst });
  const [busy, setBusy] = useState(false);
  useEffect(() => { setValues({ enabled: !!metaEnabled, first_enabled: !!metaFirst }); }, [metaEnabled, metaFirst]);
  if (metaEnabled === undefined) { return null; }  // metas pas encore chargées

  const toggle = (sw) => {
    if (READ_ONLY || !canActivate || busy || !centerId) { return; }
    const next = !values[sw.key];
    setValues((v) => ({ ...v, [sw.key]: next }));
    setBusy(true);
    api.setDispatchAuto(centerId, { [sw.key]: next })
      .then((r) => {
        setValues({ enabled: !!r.dispatch_auto_enabled, first_enabled: !!r.dispatch_auto_first_enabled });
        dispatch(showNotification("success", {
          title: sw.label, message: next ? `${sw.label} activé` : `${sw.label} désactivé`, autoDismiss: 8, dismissible: "click",
        }));
      })
      .catch((err) => {
        setValues((v) => ({ ...v, [sw.key]: !next }));  // retour à l'état réel
        const detail = err && err.response && err.response.data && err.response.data.detail;
        dispatch(showNotification("error", {
          title: sw.label, message: detail || "Impossible de basculer le dispatch auto.", autoDismiss: 10, dismissible: "click",
        }));
      })
      .then(() => setBusy(false));
  };

  return (
    <>
      {SWITCHES.map((sw) => (
        <div key={sw.key} title={(values[sw.key] ? sw.on : sw.off) + (READ_ONLY ? " Modification désactivée pour le moment : passer par l'admin." : canActivate ? "" : " (droit « Activer / désactiver le dispatch auto » requis)")}>
          [
          <input
            type="checkbox"
            checked={values[sw.key]}
            onChange={() => toggle(sw)}
            disabled={READ_ONLY || !canActivate || busy}
          /> {sw.label}
          ]
        </div>
      ))}
    </>
  );
});
DispatchAutoSwitch.propTypes = { centerId: PropTypes.number, metaEnabled: PropTypes.bool, metaFirst: PropTypes.bool, canActivate: PropTypes.bool, dispatch: PropTypes.func };
